import { action } from '@reatom/framework'

import { chartApi } from '@blade/socket-types'
import { blockHashEvent } from '@root/entities/realtime'
import {
  newTxEvent,
  newTxRafBuffer,
} from '@root/entities/realtime/atoms/new-tx-event'
import { Trade } from '@root/entities/trade/model'
import { CHART_BACKEND_URL, environmentAtom } from '@root/shared'
import { reatomSocket } from '@root/shared/api/socket'
import { reatomEventSubscription } from '@root/shared/api/socket/reatom-event-subscription'

import type { TypeHelper } from '@blade/std-lib'
import type { Network } from '@root/entities/network/model'
import type { PairAddress } from '@root/entities/pair/model'
import type { EventHandlerWithCtxMap } from '@root/shared/api/socket'

export interface SubscribeNewTxByMakerReq
  extends TypeHelper.OverrideKeys<
    Parameters<
      chartApi.ClientToServerEvents[chartApi.ClientEvent.NewTxByMakerSubscribe]
    >[0],
    { network: Network.Network }
  > {}
export interface SubscribeDataReq
  extends TypeHelper.OmitKey<
    Parameters<
      chartApi.ClientToServerEvents[chartApi.ClientEvent.NewTxSubscribe]
    >[0],
    'pairAddress'
  > {
  pairAddress: PairAddress
}
export type UnsubscribeDataReq = Parameters<
  chartApi.ClientToServerEvents[chartApi.ClientEvent.NewTxUnsubscribe]
>[0]

const listenEventsMap: EventHandlerWithCtxMap<chartApi.ServerToClientEvents> = {
  [chartApi.ServerEvent.NewTx]: (ctx, dto) => {
    const {
      assets: { tokenLogosHost, avatarsHost },
    } = ctx.get(environmentAtom)
    const trade = Trade.fromDto(dto, tokenLogosHost, avatarsHost)
    newTxEvent(ctx, trade)
    newTxRafBuffer.push(trade)
  },
  [chartApi.ServerEvent.Blockhash]: (ctx, data) => blockHashEvent(ctx, data),
  [chartApi.ServerEvent.PeriodStats]: (_ctx, __data) => {},
}

export const reatomChartWebsocket = () => {
  const { emitAction, socketAtom } = reatomSocket({
    host: CHART_BACKEND_URL,
    listenEventsMap,
  })

  // eslint-disable-next-line @reatom/unit-naming-rule
  const newTxSubscription = reatomEventSubscription({
    subscribe: (ctx, req: SubscribeDataReq) => {
      emitAction(ctx, chartApi.ClientEvent.NewTxSubscribe, req)
    },
    unsubscribe: (ctx, req: UnsubscribeDataReq) => {
      emitAction(ctx, chartApi.ClientEvent.NewTxUnsubscribe, req)
    },
    deriveKeyFromSubscribeArgs: (args) => `${args.pairAddress}`,
    deriveKeyFromUnsubscribeArgs: (args) => `${args.pairAddress}`,
  })

  // eslint-disable-next-line @reatom/unit-naming-rule
  const makerNewTxSubscription = reatomEventSubscription({
    subscribe: (ctx, req: SubscribeNewTxByMakerReq) => {
      emitAction(ctx, chartApi.ClientEvent.NewTxByMakerSubscribe, req)
    },
    unsubscribe: (ctx, req: SubscribeNewTxByMakerReq) => {
      emitAction(ctx, chartApi.ClientEvent.NewTxByMakerUnsubscribe, req)
    },
    deriveKeyFromSubscribeArgs: (args) =>
      `${args.network}_${args.makerAddress}`,
    deriveKeyFromUnsubscribeArgs: (args) =>
      `${args.network}_${args.makerAddress}`,
  })

  // eslint-disable-next-line @reatom/unit-naming-rule
  const blockHashSubscription = reatomEventSubscription({
    subscribe: (ctx, _req: undefined) => {
      emitAction(ctx, chartApi.ClientEvent.BlockhashSubscribe)
    },
    unsubscribe: (ctx, _req: undefined) => {
      emitAction(ctx, chartApi.ClientEvent.BlockhashUnsubscribe)
    },
    deriveKeyFromSubscribeArgs: () => `blockhash`,
    deriveKeyFromUnsubscribeArgs: () => `blockhash`,
  })

  const initializeAction = action((ctx) => {
    ctx.subscribe(socketAtom, (socket) => {
      if (!socket) {
        return
      }

      socket.on('connect', () => {
        if (socket.recovered) {
          return
        }

        newTxSubscription.reemitSubscriptionsActions(ctx)
        blockHashSubscription.reemitSubscriptionsActions(ctx)
        makerNewTxSubscription.reemitSubscriptionsActions(ctx)
      })
    })
  }, 'initializeAction')

  return {
    initializeAction: initializeAction,
    subscribeNewTxAction: newTxSubscription.subscribe,
    unsubscribeNewTxAction: newTxSubscription.unsubscribe,
    subscribeBlockhashAction: blockHashSubscription.subscribe,
    unsubscribeBlockhashAction: blockHashSubscription.unsubscribe,
    subscribeNewTxByMakerAction: makerNewTxSubscription.subscribe,
    unsubscribeNewTxByMakerAction: makerNewTxSubscription.unsubscribe,
  }
}

export type ChartWebsocketReatom = ReturnType<typeof reatomChartWebsocket>
